import { useDeadlinePassed } from './useDeadlinePassed';
import { useHasMounted } from './useHasMounted';
import { isWithinCountdownWindow, usePromoCountdown } from './usePromoCountdown';

/**
 * Whether the countdown banner should be treated as present on the page.
 *
 * Driven by the Builder promotions-banners model: showCountdownTimer plus the
 * startDate/endDate window. When false, the header falls back to the pencil banner.
 *
 * Until mount the window is judged against the build time, so SSR and the first
 * client render agree (no hydration mismatch) and .with-countdown reserves the
 * banner's height only when it was live at build. After mount it compares once
 * against the real clock, catching a build that landed just before the window
 * flipped; the live flip across a running page is the webhook's job
 * (scheduledStart/scheduledEnd rebuild the site at the edges, and the next
 * navigation or reload picks it up — a tab already open at End will see the
 * timer at zero until it navigates) — WEBDEV-4254, WEBDEV-4517, WEBDEV-4533.
 */
export function useCountdownActive() {
    const { countdown, buildTimeMs } = usePromoCountdown();
    const hasMounted = useHasMounted();
    const enabled = countdown !== null;

    const started = useDeadlinePassed(countdown?.startMs ?? null, enabled);
    const expired = useDeadlinePassed(countdown?.endMs ?? null, enabled);

    if (!enabled) return false;
    if (!hasMounted) return isWithinCountdownWindow(countdown, buildTimeMs);

    // Same window as isWithinCountdownWindow, judged against the real clock at
    // mount. useDeadlinePassed is a one-shot compare; rebuilds handle anything
    // after that, not a client-side timer.
    return (countdown.startMs === null || started) && !expired;
}
